<script setup lang="ts">

import {computed, Ref, ref, watch} from "vue";
import Translator from "@/utils/common/Translator.ts";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import CalenderIcon from "@/assets/widgetIcons/CalendarIcon.vue";
import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import ClockIcon from "@/assets/widgetIcons/ClockIcon.vue";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import {date, QInput, QPopupProxy} from "quasar";
import dayjs, {ISO_DATE_FORMATS} from "@/utils/dayjs";
import {focusAndSelectInput} from "@/utils/common/NamaVueWidget.ts";

const props = defineProps<WidgetProps>();
const fieldId = props.field.id!;
const fieldUtil = new FieldWidgetUtil(props.presenter, props.fieldMetaData);

const padZero = (num: number) => (num < 10 ? `0${num}` : num);
const today = new Date();
const formattedDate = `${today.getFullYear()}/${padZero(today.getMonth() + 1)}`;
const defaultDate = ref(formattedDate);
const dateProxy = ref<QPopupProxy>();

const dateAndTime = fieldUtil.formattedValue();
const selectedDate: Ref<any | null> = ref(dateAndTime.value ? dateAndTime.value.split(" ")[0] : '');
const selectedTime: Ref<any | null> = ref(dateAndTime.value ? dateAndTime.value.split(" ")[1] : '');

const selectedDateForTitle = computed(() => {
  let timeStamp: string = selectedDate.value ? selectedDate.value : defaultDate.value;
  return date.formatDate(dayjs(timeStamp, ISO_DATE_FORMATS).format("YYYY-MM-DD"), Translator.isArabic() ? 'dddd, D MMMM' : 'dddd, D MMM');
})

watch(
  () => dateAndTime.value,
  (value) => {
    selectedDate.value = value.split(" ")[0]
    selectedTime.value = value.split(" ")[1]
  }
);

function onDateSelect(value: any) {
  fieldUtil.onPartialUserInput(`${value} ${selectedTime.value}`);
  selectedDate.value = value;
  dateProxy.value?.hide();
}

function onTimeSelect(value: any) {
  fieldUtil.onPartialUserInput(`${selectedDate.value} ${value}`);
  selectedTime.value = value;
}
const input = ref<QInput>()

function focus() {
  focusAndSelectInput(input);
}

function canReceiveFocus() {
  return !fieldUtil.isFieldDisabled(props.forceEnableField);
}

function onF4KeyClicked(e) {
  if (dateProxy.value)
    dateProxy.value.toggle(e)
}

function onDoubleClick(e: MouseEvent) {
  if (fieldUtil.isFieldDisabled(props.forceEnableField))
    return;
  dateProxy.value?.show(e);
}

defineExpose({focus, canReceiveFocus})
</script>

<template>
  <WidgetContainer
    :field="field"
    :entityType="presenter.entityType"
    :isSpanned="isSpanned" :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)"
    :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="isInsideComposite">
    <template v-slot:widget="{ showAsPassword, doNotGoToNextWithEnter }">
      <q-input hide-bottom-space outlined dense :model-value="dateAndTime" @update:model-value="fieldUtil.onPartialUserInput"
               :readonly="fieldUtil.isFieldDisabled(forceEnableField)" ref="input"
               @focus="fieldUtil.onFocus" @blur="fieldUtil.onBlur"
               @dblclick="onDoubleClick"
               :id="fieldId"
               autocomplete="off"
               :type="showAsPassword ? 'password' : 'text'"
               :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
               class="nama-customize-dense-input nama-focused-input nama-dark-popup"
               :class="{'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField)}"
               input-class="nama-input-class"
               for="dateAndTimeFieldWidget"
               @keydown.enter="(e)=>{ if (!doNotGoToNextWithEnter) $emit('navigateToNext',{keyboardEvent:e}) }"
               @keydown.f4.stop="onF4KeyClicked">
        <template v-slot:append>
          <q-avatar square name="event" class="tw-cursor-pointer nama-avatar-hover">
            <CalenderIcon class="tw-size-4 nama-widget-icon-hover nama-widget-icon"/>
            <q-popup-proxy ref="dateProxy" cover class="tw-bg-transparent ag-custom-component-popup" transition-show="scale" transition-hide="scale"
                           @hide="fieldUtil.onUserInputFinished" @keydown.f4.stop="onF4KeyClicked">
              <q-date mask="YYYY-MM-DD" :model-value="selectedDate" @update:model-value="onDateSelect" :title="selectedDateForTitle"
                      :default-year-month="defaultDate" :readonly="fieldUtil.isFieldDisabled(forceEnableField)" today-btn
                      class="tw-text-namaBlack tw-bg-namaBackground-deep-light dark:tw-text-white dark:tw-bg-namaBackground-dim-dark">
                <div class="tw-bg-transparent row items-center justify-end">
                  <q-btn v-close-popup :label="Translator.translateId('close')" class="tw-text-primary-light dark:tw-text-primary-dark tw-bg-transparent" flat/>
                </div>
              </q-date>
            </q-popup-proxy>
          </q-avatar>
          <q-avatar square name="event" class="tw-cursor-pointer nama-avatar-hover">
            <ClockIcon class="tw-size-4  nama-widget-icon-hover nama-widget-icon"/>
            <q-popup-proxy cover class="tw-bg-transparent ag-custom-component-popup" transition-show="scale" transition-hide="scale" @hide="fieldUtil.onUserInputFinished">
              <q-time :model-value="selectedTime" @update:model-value="onTimeSelect" format24h now-btn
                      :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
                      class="tw-text-namaBlack tw-bg-namaBackground-deep-light dark:tw-text-white dark:tw-bg-namaBackground-dim-dark">
                <div class="tw-bg-transparent row items-center justify-end">
                  <q-btn v-close-popup :label="Translator.translateId('close')" class="tw-text-primary-light dark:tw-text-primary-dark tw-bg-transparent" flat/>
                </div>
              </q-time>
            </q-popup-proxy>
          </q-avatar>
        </template>
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </q-input>
    </template>
  </WidgetContainer>
</template>
<style>

</style>
